// Trends page — shared labels, colour tokens and small SVG pieces (sparkline, drawer chart,
// state chip). Everything numeric comes from GET /api/gps/trends (services/trends/); nothing here
// computes a trend, it only maps states to visuals.
import React, { useState } from 'react';

export const METRICS = [
  { key: 'td', label: 'TD', unit: 'm' },
  { key: 'rd', label: 'RD', unit: 'm' },
  { key: 'hid', label: 'HID', unit: 'm' },
  { key: 'sd', label: 'SD', unit: 'm' },
  { key: 'sa', label: 'SA', unit: '' },
  { key: 'expl', label: 'Expl.', unit: 'm' },
  { key: 'hmld', label: 'HMLD', unit: 'm' },
];
export const BLOCK_LABEL = { match: 'Match', training: 'Training' };
export const POSITION_ORDER = ['CB', 'FB', 'CDM', 'CAM', 'W', 'ST'];
export const HIGH_VARIATION_METRICS = ['hid', 'sd'];
export const HIGH_VARIATION_NOTE = 'High variation — stable means: no demonstrable trend';
export const NOT_APPLICABLE_LABEL = 'n/a — too little sprint volume in training for a trend';
// States that claim no trend: draw the points only, never a fit line or stable zone.
export const NO_TREND_STATES = ['insufficient', 'rehab', 'not_applicable'];

export const EXCLUDED_LABEL = { rehab: 'rehab', estimated: 'estimated', no_training: 'no training', zero: '0' };

// ── Colour tokens ──────────────────────────────────────────────────────────────────────────────
// Colour means DIRECTION only (blue up, orange down), never good/bad. Clear = solid chip in the
// hue, weak = light tint of the same hue with a hue border, stable = grey, irregular = grey hatch.
// Validated 2026-09-28 (dataviz validate_palette.js + WCAG): blue/orange pass lightness, chroma,
// CVD (ΔE ≈ 30) and >= 3:1 vs the card surface in both themes; every arrow-on-chip pair >= 4.5:1.
// The % next to a chip stays in text ink — colour is never the only signal.
export const TRENDS_CSS = `
.trends-root {
  --tr-up-solid: #2563eb; --tr-down-solid: #c2410c;
  --tr-up-tint: rgba(59,130,246,0.20); --tr-down-tint: rgba(234,88,12,0.20);
  --tr-up-ink: #93c5fd; --tr-down-ink: #fdba74;
  --tr-up-line: #60a5fa; --tr-down-line: #fb923c;
  --tr-stable: #94a3b8; --tr-hatch: rgba(148,163,184,0.38);
  --tr-band: rgba(148,163,184,0.14); --tr-ref: #64748b;
}
[data-theme="day"] .trends-root {
  --tr-up-solid: #1d4ed8; --tr-down-solid: #c2410c;
  --tr-up-tint: #dbeafe; --tr-down-tint: #ffedd5;
  --tr-up-ink: #1d4ed8; --tr-down-ink: #c2410c;
  --tr-up-line: #1d4ed8; --tr-down-line: #c2410c;
  --tr-stable: #64748b; --tr-hatch: rgba(100,116,139,0.35);
  --tr-band: rgba(100,116,139,0.12); --tr-ref: #94a3b8;
}
.trends-root .tr-chip { display: inline-flex; align-items: center; justify-content: center; width: 20px; height: 20px;
  border-radius: 5px; font-size: 12px; font-weight: 800; line-height: 1; flex: 0 0 auto; box-sizing: border-box; }
.trends-root .tr-clear-up { background: var(--tr-up-solid); color: #fff; }
.trends-root .tr-clear-down { background: var(--tr-down-solid); color: #fff; }
.trends-root .tr-weak-up { background: var(--tr-up-tint); color: var(--tr-up-ink); border: 1px solid var(--tr-up-line); }
.trends-root .tr-weak-down { background: var(--tr-down-tint); color: var(--tr-down-ink); border: 1px solid var(--tr-down-line); }
.trends-root .tr-stable { color: var(--tr-stable); border: 1px solid var(--border-strong); }
.trends-root .tr-irregular { color: var(--text-secondary); border: 1px solid var(--border-strong);
  background: repeating-linear-gradient(45deg, var(--tr-hatch) 0 2px, transparent 2px 5px); }
.trends-root .tr-row { cursor: pointer; }
.trends-root .tr-row:hover td, .trends-root .tr-row:focus-visible td { background: var(--bg-row-hover); }
.trends-root .tr-row:focus-visible { outline: 2px solid var(--tr-up-line); outline-offset: -2px; }
.trends-root .tr-skel { background: var(--border); border-radius: 6px; animation: tr-pulse 1.2s ease-in-out infinite; }
@keyframes tr-pulse { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }
`;

// ── State → visual ─────────────────────────────────────────────────────────────────────────────
// Returns { cls, arrow, word } for a metric state; word is the state name for tooltips.
export function stateVisual(state) {
  switch (state) {
    case 'clear_up': return { cls: 'tr-clear-up', arrow: '↑', word: 'clearly rising', color: 'var(--tr-up-line)' };
    case 'weak_up': return { cls: 'tr-weak-up', arrow: '↗', word: 'weakly rising', color: 'var(--tr-up-line)' };
    case 'clear_down': return { cls: 'tr-clear-down', arrow: '↓', word: 'clearly falling', color: 'var(--tr-down-line)' };
    case 'weak_down': return { cls: 'tr-weak-down', arrow: '↘', word: 'weakly falling', color: 'var(--tr-down-line)' };
    case 'stable': return { cls: 'tr-stable', arrow: '→', word: 'stable', color: 'var(--tr-stable)' };
    case 'irregular': return { cls: 'tr-irregular', arrow: '~', word: 'irregular', color: 'var(--tr-stable)' };
    case 'not_applicable': return { cls: null, arrow: null, word: 'n/a', color: 'var(--tr-stable)' };
    case 'rehab': return { cls: null, arrow: null, word: 'rehab', color: 'var(--tr-stable)' };
    default: return { cls: null, arrow: null, word: 'insufficient data', color: 'var(--tr-stable)' };
  }
}

export function StateChip({ state }) {
  const v = stateVisual(state);
  if (!v.cls) return null;
  return <span className={`tr-chip ${v.cls}`} aria-hidden="true">{v.arrow}</span>;
}

// Signed, no decimals, typographic minus — same format as the conclusions.
export function fmtPct(v) {
  if (v == null || isNaN(v)) return '—';
  const r = Math.round(v);
  if (r === 0) return '0%';
  return `${r > 0 ? '+' : '−'}${Math.abs(r)}%`;
}
export const fmtDate = d => (d ? d.slice(0, 10).split('-').reverse().join('-') : '—');
export const fmtShortDate = d => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}` : '');
export const weekNo = isoWeek => Number(String(isoWeek).split('-W')[1]);

export function fmtValue(block, metricKey, v) {
  if (v == null || isNaN(v)) return '—';
  if (block === 'training') return `${v.toFixed(2)}× median`;
  const unit = METRICS.find(m => m.key === metricKey)?.unit;
  return `${Math.round(v).toLocaleString('en-GB')}${unit ? ` ${unit}` : ''} /90`;
}

// One slot per window position (8), in window order. value = per-90 (match) or ratio (training);
// excluded = reason or null; started only for training.
export function blockSlots(player, block, metricKey) {
  if (block === 'match') {
    return (player.match.points || []).map((p, idx) => ({
      idx, value: p.values ? p.values[metricKey] : null, excluded: p.excluded || null, started: true,
      xLabel: fmtShortDate(p.date), hover: `${fmtDate(p.date)} · ${p.opponent || '—'} · ${Math.round(p.minutes)} min`,
    }));
  }
  return (player.training.weeks || []).map((w, idx) => ({
    idx, value: w.ratios ? w.ratios[metricKey] : null,
    excluded: w.excluded || (w.excludedMetrics && w.excludedMetrics[metricKey]) || null, started: !!w.started,
    xLabel: `W${weekNo(w.isoWeek)}`, hover: `Week ${weekNo(w.isoWeek)} (from ${fmtDate(w.start)})${w.started ? ' · started a match' : ''}`,
  }));
}

// Cell tooltip: value, band, n (+ HID/SD note).
export function cellTitle(block, metricKey, mm, lastValue) {
  const v = stateVisual(mm.state);
  const lines = [
    `${METRICS.find(m => m.key === metricKey).label} — ${BLOCK_LABEL[block]}: ${mm.state === 'not_applicable' ? NOT_APPLICABLE_LABEL : v.word}`,
    `Change over the window: ${fmtPct(mm.changePct)}`,
    `Latest value: ${fmtValue(block, metricKey, lastValue)}`,
    `Band: stable < ${mm.bandP80}% · clear ≥ ${mm.bandP95}% · n = ${mm.n}`,
  ];
  if (HIGH_VARIATION_METRICS.includes(metricKey) && mm.state !== 'not_applicable') lines.push(HIGH_VARIATION_NOTE);
  return lines.join('\n');
}

// ── Sparkline (table cell) ─────────────────────────────────────────────────────────────────────
// 8 slots, raw points as dots (thin grey connector), Theil-Sen fit in the state's colour.
// Excluded points hollow and dimmed; in training, started weeks filled and the others open.
export function Sparkline({ slots, fitPoints, state, block, width = 64, height = 20, windowSize = 8 }) {
  const vals = [...slots.map(s => s.value), ...(fitPoints || []).map(p => p.fitted)].filter(v => v != null && isFinite(v));
  if (!vals.length) return <svg width={width} height={height} aria-hidden="true" />;
  const pad = 3;
  let lo = Math.min(...vals), hi = Math.max(...vals);
  if (hi - lo < 1e-9) { lo -= 1; hi += 1; }
  const x = i => pad + (i * (width - 2 * pad)) / Math.max(1, windowSize - 1);
  const y = v => height - pad - ((v - lo) / (hi - lo)) * (height - 2 * pad);
  const lineColor = stateVisual(state).color;
  const valid = slots.filter(s => s.value != null && !s.excluded);
  return (
    <svg width={width} height={height} aria-hidden="true" style={{ display: 'block', overflow: 'visible' }}>
      {valid.length > 1 && (
        <polyline points={valid.map(s => `${x(s.idx)},${y(s.value)}`).join(' ')} fill="none" stroke="var(--border-strong)" strokeWidth="1" />
      )}
      {fitPoints && fitPoints.length > 1 && (
        <polyline points={fitPoints.map(p => `${x(p.idx)},${y(p.fitted)}`).join(' ')} fill="none" stroke={lineColor} strokeWidth="2" strokeLinecap="round" />
      )}
      {slots.filter(s => s.value != null).map(s => {
        const hollow = !!s.excluded || (block === 'training' && !s.started);
        return (
          <circle key={s.idx} cx={x(s.idx)} cy={y(s.value)} r={2}
            fill={hollow ? 'var(--bg-card)' : 'var(--text-secondary)'}
            stroke={s.excluded ? 'var(--text-dimmed)' : 'var(--text-secondary)'}
            strokeWidth="1" strokeDasharray={s.excluded ? '1.5 1' : undefined} opacity={s.excluded ? 0.7 : 1} />
        );
      })}
    </svg>
  );
}

// ── Drawer chart (one metric, one block) ───────────────────────────────────────────────────────
// Points, fitted line, the stable zone and — for training — the squad median at 1.0. The stable
// zone is mean × (1 ± p80/2 %): a fitted line whose total change stays under p80 fits inside it,
// which is exactly the "stable" rule; one that leaves it is at least a weak trend.
export function MetricChart({ slots, mm, block, metricKey, width = 300, height = 116 }) {
  const [hover, setHover] = useState(null);
  const showTrend = !NO_TREND_STATES.includes(mm.state);
  const fit = showTrend ? (mm.points || []) : [];
  const validVals = fit.map(p => p.value);
  const mean = validVals.length ? validVals.reduce((a, b) => a + b, 0) / validVals.length : null;
  const half = mean != null ? mean * (mm.bandP80 / 200) : null;
  const vals = [...slots.map(s => s.value), ...fit.map(p => p.fitted)].filter(v => v != null && isFinite(v));
  if (mean != null && half != null) vals.push(mean - half, mean + half);
  if (block === 'training') vals.push(1);
  // Side padding leaves room for the first/last x label (centred on its point).
  const padL = 18, padR = 18, padT = 12, padB = 22;
  if (!vals.length) {
    return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'var(--text-muted)' }}>No data</div>;
  }
  let lo = Math.min(...vals), hi = Math.max(...vals);
  const span = hi - lo || Math.abs(hi) || 1;
  lo -= span * 0.08; hi += span * 0.08;
  const n = Math.max(slots.length, 8);
  const x = i => padL + (i * (width - padL - padR)) / Math.max(1, n - 1);
  const y = v => padT + (1 - (v - lo) / (hi - lo)) * (height - padT - padB);
  const lineColor = stateVisual(mm.state).color;
  const valid = slots.filter(s => s.value != null && !s.excluded);
  const hovered = hover != null ? slots.find(s => s.idx === hover) : null;

  return (
    <div style={{ position: 'relative' }} onMouseLeave={() => setHover(null)}>
      <svg width="100%" viewBox={`0 0 ${width} ${height}`} role="img"
        aria-label={`${METRICS.find(m => m.key === metricKey).label} ${BLOCK_LABEL[block]}: ${stateVisual(mm.state).word}, ${fmtPct(mm.changePct)}`}>
        {showTrend && mean != null && half != null && (
          <rect x={padL} width={width - padL - padR} y={y(mean + half)} height={Math.max(1, y(mean - half) - y(mean + half))} fill="var(--tr-band)" />
        )}
        {block === 'training' && (
          <g>
            <line x1={padL} x2={width - padR} y1={y(1)} y2={y(1)} stroke="var(--tr-ref)" strokeWidth="1" strokeDasharray="3 3" />
            <text x={2} y={y(1) - 3} textAnchor="start" fontSize="8.5" fill="var(--text-muted)">1.0</text>
          </g>
        )}
        {valid.length > 1 && (
          <polyline points={valid.map(s => `${x(s.idx)},${y(s.value)}`).join(' ')} fill="none" stroke="var(--border-strong)" strokeWidth="1" />
        )}
        {fit.length > 1 && (
          <polyline points={fit.map(p => `${x(p.idx)},${y(p.fitted)}`).join(' ')} fill="none" stroke={lineColor} strokeWidth="2" strokeLinecap="round" />
        )}
        {slots.map(s => {
          const cx = x(s.idx);
          const hollow = !!s.excluded || (block === 'training' && !s.started);
          return (
            <g key={s.idx}>
              {/* Hit target wider than the mark: the whole slot column. */}
              <rect x={cx - (width - padL - padR) / (2 * Math.max(1, n - 1))} width={(width - padL - padR) / Math.max(1, n - 1)}
                y={0} height={height - padB + 4} fill="transparent" onMouseEnter={() => setHover(s.idx)} />
              {s.value != null && (
                <circle cx={cx} cy={y(s.value)} r={hover === s.idx ? 4.5 : 3.5}
                  fill={hollow ? 'var(--bg-card)' : 'var(--text-primary)'}
                  stroke={s.excluded ? 'var(--text-dimmed)' : 'var(--text-primary)'} strokeWidth="1.5"
                  strokeDasharray={s.excluded ? '2 1.5' : undefined} pointerEvents="none" />
              )}
              {s.excluded && (
                <text x={cx} y={s.value != null ? y(s.value) - 7 : height - padB - 4} textAnchor="middle" fontSize="8.5" fill="var(--text-muted)" pointerEvents="none">
                  {EXCLUDED_LABEL[s.excluded] || s.excluded}
                </text>
              )}
              <text x={cx} y={height - 6} textAnchor="middle" fontSize="9" fill="var(--text-muted)" pointerEvents="none">{s.xLabel}</text>
            </g>
          );
        })}
      </svg>
      {hovered && (
        <div style={{
          position: 'absolute', top: 0, left: `${(x(hovered.idx) / width) * 100}%`, transform: `translateX(${hovered.idx > n / 2 ? '-100%' : '0'})`,
          background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 6, padding: '6px 8px',
          fontSize: 11, color: 'var(--text-primary)', whiteSpace: 'nowrap', boxShadow: '0 6px 18px rgba(0,0,0,0.25)', pointerEvents: 'none', zIndex: 2,
        }}>
          <div style={{ color: 'var(--text-muted)' }}>{hovered.hover}</div>
          <div style={{ fontWeight: 700 }}>{fmtValue(block, metricKey, hovered.value)}</div>
          {hovered.excluded && <div style={{ color: 'var(--text-muted)' }}>Not counted: {EXCLUDED_LABEL[hovered.excluded] || hovered.excluded}</div>}
        </div>
      )}
    </div>
  );
}
